/**
 * 発注管理ページ(独立メイン画面)
 *
 * 設計書 05-frontend-design.md §5.1 整合。
 * `usePurchaseOrders` フックでデータ取得、`PurchaseOrderTable` / `PurchaseOrderFilterPanel` で表示、
 * 4 モーダル(`activeModal` 単一 state)で操作を統合する。
 * ファイル肥大化を避けるため、200 行を目安としてフックとサブコンポーネントに責務分離。
 */
import React, { useState } from 'react';
import { Alert, Button, Card, Flex, Space } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useAuthStore } from '@/features/auth/stores/authStore';
import {
  canCreatePurchaseOrder,
  canConfirmArrival as hasConfirmArrivalPermission,
  canReverseArrival as hasReverseArrivalPermission,
  canCancelPurchaseOrder as hasCancelPurchaseOrderPermission,
  isAdmin,
} from '@/features/auth/utils/permissionUtils';
import { usePurchaseOrders } from '@/features/purchase-orders/hooks/usePurchaseOrders';
import { useMaterialTypeOptions } from '@/features/purchase-orders/hooks/useMaterialTypeOptions';
import { useAccessibleSiteOptions } from '@/features/sites/hooks/useAccessibleSiteOptions';
import { buildSiteSelectionContext } from '@/features/purchase-orders/utils/siteOptionsBuilder';
import { PurchaseOrderTable } from '@/features/purchase-orders/components/PurchaseOrderTable';
import { PurchaseOrderFilterPanel } from '@/features/purchase-orders/components/PurchaseOrderFilterPanel';
import { PurchaseOrderRegistrationModal } from '@/features/purchase-orders/components/PurchaseOrderRegistrationModal';
import { ArrivalConfirmationModal } from '@/features/purchase-orders/components/ArrivalConfirmationModal';
import { ArrivalReversalModal } from '@/features/purchase-orders/components/ArrivalReversalModal';
import { PurchaseOrderCancelModal } from '@/features/purchase-orders/components/PurchaseOrderCancelModal';
import type { PurchaseOrderViewModel } from '@/features/purchase-orders/types/purchaseOrder';

/**
 * 4 モーダルの種別を1変数で管理(設計書 §5.1 useState 激増回避)
 */
type ActiveModalKey = 'register' | 'arrival' | 'reversal' | 'cancel' | null;

export const PurchaseOrderListPage: React.FC = () => {
  const { user } = useAuthStore();
  const {
    items,
    pagination,
    pageSize,
    isLoading,
    error,
    filter,
    setFilter,
    setPage,
    reload,
    dismissError,
  } = usePurchaseOrders();
  const { materialTypeOptions, error: materialTypeError } = useMaterialTypeOptions();
  const { accessibleSites, error: accessibleSitesError } = useAccessibleSiteOptions({ user });

  const [activeModal, setActiveModal] = useState<ActiveModalKey>(null);
  const [selectedOrder, setSelectedOrder] = useState<PurchaseOrderViewModel | null>(null);
  const [
    isMaterialTypeErrorDismissed,
    setIsMaterialTypeErrorDismissed,
  ] = useState<boolean>(false);

  const isUserAdmin = isAdmin(user);

  // 現場関連の選択肢/ラベルは utils 純粋関数で 1 構造に集約導出。
  // 管理者(MCM/DEV)は `useAccessibleSiteOptions` で取得した全現場を、
  // SITE_*(非管理者)は `user.site` + items フォールバックで自サイトを使用。
  const siteSelectionContext = buildSiteSelectionContext({
    user,
    accessibleSites,
    items,
  });

  /** モーダルを閉じて選択行をクリア(リロードなし) */
  const closeActiveModal = (): void => {
    setActiveModal(null);
    setSelectedOrder(null);
  };

  /**
   * モーダルを閉じて一覧を再取得(各モーダル完了 payload は未使用 = reload で全件再取得)。
   * reload 失敗時の error は usePurchaseOrders.error に集約され Alert で表示される。
   */
  const closeModalAndReload = async (): Promise<void> => {
    closeActiveModal();
    await reload();
  };

  return (
    <>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {canCreatePurchaseOrder(user) && (
          <Flex justify="end">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setActiveModal('register')}
            >
              新規発注
            </Button>
          </Flex>
        )}

        {error !== null && (
          <Alert
            message="エラー"
            description={error.message}
            type="error"
            showIcon
            closable
            onClose={dismissError}
          />
        )}

        {materialTypeError !== null && !isMaterialTypeErrorDismissed && (
          <Alert
            message="エラー"
            description={materialTypeError.message}
            type="error"
            showIcon
            closable
            onClose={() => setIsMaterialTypeErrorDismissed(true)}
          />
        )}

        {accessibleSitesError !== null && (
          <Alert
            message="エラー"
            description={`現場一覧の取得に失敗しました: ${accessibleSitesError.message}`}
            type="error"
            showIcon
          />
        )}

        <Card>
          <PurchaseOrderFilterPanel
            currentFilter={filter}
            materialTypeOptions={materialTypeOptions}
            siteFilterMode={siteSelectionContext.filterMode}
            user={user}
            onFilterChange={(params) => setFilter({ filter: params.filter })}
          />
        </Card>

        <PurchaseOrderTable
          items={items}
          pagination={pagination}
          pageSize={pageSize}
          isLoading={isLoading}
          showsSiteColumn={isUserAdmin}
          canConfirmArrival={hasConfirmArrivalPermission(user)}
          canReverseArrival={hasReverseArrivalPermission(user)}
          canCancelPurchaseOrder={hasCancelPurchaseOrderPermission(user)}
          onConfirmArrival={({ target }) => {
            setSelectedOrder(target);
            setActiveModal('arrival');
          }}
          onReverseArrival={({ target }) => {
            setSelectedOrder(target);
            setActiveModal('reversal');
          }}
          onCancelPurchaseOrder={({ target }) => {
            setSelectedOrder(target);
            setActiveModal('cancel');
          }}
          onPageChange={({ page }) => setPage({ page })}
        />
      </Space>

      {/* 非表示中に Form インスタンスへアクセスしないよう、表示中のみマウントする */}
      {activeModal === 'register' && (
        <PurchaseOrderRegistrationModal
          open={activeModal === 'register'}
          onClose={closeActiveModal}
          onCreated={() => {
            void closeModalAndReload();
          }}
          siteOptions={siteSelectionContext.registrationOptions}
          materialTypeOptions={materialTypeOptions}
          fixedSiteId={siteSelectionContext.fixedRegistrationSiteId}
        />
      )}

      {selectedOrder !== null && (
        <>
          <ArrivalConfirmationModal
            open={activeModal === 'arrival'}
            onClose={closeActiveModal}
            onConfirmed={() => {
              void closeModalAndReload();
            }}
            purchaseOrder={selectedOrder}
          />
          <ArrivalReversalModal
            open={activeModal === 'reversal'}
            onClose={closeActiveModal}
            onReversed={() => {
              void closeModalAndReload();
            }}
            purchaseOrder={selectedOrder}
          />
          <PurchaseOrderCancelModal
            open={activeModal === 'cancel'}
            onClose={closeActiveModal}
            onCancelled={() => {
              void closeModalAndReload();
            }}
            purchaseOrder={selectedOrder}
          />
        </>
      )}
    </>
  );
};
